<template>
  <div class="employees-sticky-header surface-0 mb-2">
    <div class="employees-header-summary flex align-items-center">
      <div class="py-2">
        <h2 class="m-0 p-0 font-light text-600 text-1xl">Employees</h2>
        <p class="p-0 m-0 pt-1 text-xs text-400">
          Showing {{ employeeRows.length }} of {{ employeePagination.total }} results.
        </p>
      </div>
    </div>

    <div class="employees-filters">
      <InputText
        v-model="employeeName"
        size="small"
        placeholder="Employee name"
        :disabled="isLoading"
        class="w-full md:w-13rem"
        @keyup.enter="applyFilters"
      />
      <Select
        v-model="selectedCompanyId"
        :options="companiesList"
        optionLabel="company_name"
        optionValue="company_id"
        filter
        showClear
        size="small"
        filterPlaceholder="Search companies"
        placeholder="All Companies"
        :loading="companiesStore.companiesLoading"
        :disabled="isLoading || companiesStore.companiesLoading"
        class="w-full md:w-13rem"
      />
      <Select
        v-model="selectedStatus"
        :options="statusOptions"
        optionLabel="name"
        optionValue="code"
        size="small"
        placeholder="Status"
        :disabled="isLoading"
        class="w-full md:w-10rem"
      />
      <Select
        v-model="selectedStructure"
        :options="structuresList"
        optionLabel="name"
        optionValue="code"
        filter
        showClear
        size="small"
        filterPlaceholder="Search structures"
        placeholder="All Structures"
        :loading="structuresLoading"
        :disabled="isLoading || structuresLoading"
        class="w-full md:w-12rem"
      />
      <Calendar
        v-model="contractDateRange"
        selectionMode="range"
        :showIcon="true"
        :showClear="true"
        :manualInput="false"
        :hideOnRangeSelection="true"
        dateFormat="yy-mm-dd"
        size="small"
        placeholder="Contract Date Range"
        :disabled="isLoading || showIncorrectContractDates"
        class="w-full md:w-15rem"
      />
      <label for="incorrect-contract-dates" class="flex align-items-center gap-2 cursor-pointer white-space-nowrap">
        <Checkbox
          v-model="showIncorrectContractDates"
          inputId="incorrect-contract-dates"
          binary
          :disabled="isLoading"
        />
        <span>Incorrect contract dates</span>
      </label>
      <Button
        icon="pi pi-search"
        label="Apply"
        size="small"
        :loading="isLoading"
        :disabled="!hasCompleteDateRange || structuresLoading"
        @click="applyFilters"
      />
      <Button
        icon="pi pi-plus"
        label="Add Employee"
        size="small"
        class="surface-300 hover:surface-500"
        :disabled="isLoading"
        @click="showAddDialog"
      />
    </div>
  </div>

  <Message v-if="displayError" severity="error" :closable="false" class="mb-2">
    {{ displayError }}
  </Message>

  <div v-if="selectedEmployeeRow" class="employee-action shadow-8">
    <Button
      icon="pi pi-pencil"
      label="Edit Employee"
      class="px-4 py-3 text-lg surface-300 hover:surface-500"
      @click="showEditDialog"
    />
  </div>

  <DataTable
    :value="employeeRows"
    v-model:selection="selectedEmployeeRow"
    selectionMode="single"
    dataKey="employee_id"
    lazy
    size="small"
    :loading="isLoading"
    :sortField="'employee_name'"
    :sortOrder="1"
    tableStyle="min-height: 20rem"
  >
    <Column field="employee_name" header="Employee" class="text-600 w-2">
      <template #body="slotProps">
        <div class="flex items-center gap-2">
          <img
            alt="Employee"
            :src="slotProps.data.employee_photo_url || defaultUserImage"
            class="employee-photo"
          />
          <div>
            <span class="block font-bold pb-1">{{ slotProps.data.employee_name }}</span>
            <span class="block text-xs text-300">{{ slotProps.data.employee_company_name }}</span>
            <span class="text-xs text-300">@{{ slotProps.data.employee_id }}</span>
          </div>
        </div>
      </template>
    </Column>
    <Column field="employee_department" header="Department" class="text-600 w-1">
      <template #body="slotProps">
        <span class="block pb-1">{{ slotProps.data.employee_department }}</span>
        <span class="block text-xs text-300">{{ slotProps.data.employee_status }}</span>
        <span class="block text-xs text-300">{{ slotProps.data.employee_contract }}</span>
      </template>
    </Column>
    <Column field="employee_join_date" header="Joined / Contract" class="text-600">
      <template #body="slotProps">
        <span class="block pb-1">Joined: {{ slotProps.data.employee_join_date || 'Not set' }}</span>
        <span
          class="block text-xs"
          :class="isIncorrectContractDate(slotProps.data) ? 'text-red-400 font-bold' : 'text-300'"
        >
          Contract: {{ slotProps.data.employee_contract_date || 'Not set' }}
        </span>
      </template>
    </Column>
    <Column field="employee_badge_id" header="Badge ID" class="text-600">
      <template #body="slotProps">
        <span class="block pb-1">Badge #{{ slotProps.data.employee_badge_id }}</span>
        <span class="block text-xs text-300">Pin #{{ slotProps.data.employee_badge_pin }}</span>
      </template>
    </Column>
    <Column field="tenant_id" header="Tenant" class="text-600">
      <template #body="slotProps">
        <span>{{ slotProps.data.tenant_id }}</span>
      </template>
    </Column>
    <Column field="employee_phone_number_main" header="Phone Number" class="text-600">
      <template #body="slotProps">
        <span class="block pb-1">{{ slotProps.data.employee_phone_number_main }}</span>
        <span class="block text-xs text-300">{{ slotProps.data.employee_phone_number_secondary }}</span>
      </template>
    </Column>
    <Column field="employee_next_of_kin" header="Next of Kin" class="text-600">
      <template #body="slotProps">
        <span class="block pb-1">{{ slotProps.data.employee_next_of_kin_number }}</span>
        <span class="block text-xs text-300">{{ slotProps.data.employee_next_of_kin }}</span>
      </template>
    </Column>

    <template #empty>
      <div class="text-center text-400 py-6">No employees found for these filters.</div>
    </template>
  </DataTable>

  <Paginator
    v-if="employeePagination.total > 0"
    :rows="pageSize"
    :first="first"
    :totalRecords="employeePagination.total"
    :rowsPerPageOptions="[50, 100]"
    @page="onPage"
  />

  <Dialog v-model:visible="editEmployeeDialog" :style="{ width: '60vw' }" :modal="true">
    <template #header><h2>Edit Employee</h2></template>
    <EmployeeUpdateDialog
      v-if="selectedEmployeeRow"
      :selectedId="selectedEmployeeRow.employee_id"
      @handleBack="handleBack"
      @employeeUpdated="handleEmployeeUpdated"
    />
  </Dialog>
  <Dialog v-model:visible="addEmployeeDialog" :style="{ width: '60vw' }" :modal="true">
    <template #header><h2>Add Employee</h2></template>
    <EmployeeAddDialog
      @handleBack="handleBack"
      @employeeCreated="handleEmployeeUpdated"
    />
  </Dialog>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import Checkbox from 'primevue/checkbox';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Paginator from 'primevue/paginator';
import Select from 'primevue/select';
import EmployeeAddDialog from '@/components/dialogs/EmployeeAddDialog.vue';
import EmployeeUpdateDialog from '@/components/dialogs/EmployeeUpdateDialog.vue';
import { useEmployeeSearch } from '@/composables/api/useEmployeeSearch';
import { useCompaniesStore } from '@/stores/companies';
import { useEmployeesStore } from '@/stores/employees';
import { useStructuresStore } from '@/stores/structures';
import defaultUserImage from '@/assets/default-user.png';

const toast = useToast();
const companiesStore = useCompaniesStore();
const employeesStore = useEmployeesStore();
const structuresStore = useStructuresStore();
const { companiesList } = storeToRefs(companiesStore);
const { structuresList, structuresLoading, structuresError } = storeToRefs(structuresStore);
const {
  employees: employeeRows,
  pagination: employeePagination,
  loading,
  error,
  searchEmployees,
} = useEmployeeSearch();

const statusOptions = [
  { name: 'All', code: 'ALL' },
  { name: 'Active', code: 'ACTIVE' },
  { name: 'Permanent', code: 'PERMANENT' },
  { name: 'Probation', code: 'PROBATION' },
  { name: 'Trainee', code: 'TRAINEE' },
  { name: 'Resigned', code: 'RESIGNED' },
  { name: 'Terminated', code: 'TERMINATED' },
  { name: 'On Hold', code: 'ON_HOLD' },
];

const selectedEmployeeRow = ref(null);
const editEmployeeDialog = ref(false);
const addEmployeeDialog = ref(false);
const employeeName = ref('');
const selectedCompanyId = ref(null);
const selectedStatus = ref('ACTIVE');
const selectedStructure = ref(null);
const contractDateRange = ref(null);
const showIncorrectContractDates = ref(false);
const appliedFilters = ref(null);
const first = ref(0);
const pageSize = ref(100);
const refreshing = ref(false);

const isLoading = computed(() => loading.value || refreshing.value);
const displayError = computed(() => error.value || structuresError.value);
const hasCompleteDateRange = computed(() => (
  showIncorrectContractDates.value
  || !contractDateRange.value
  || (
    Array.isArray(contractDateRange.value)
    && contractDateRange.value.length === 2
    && contractDateRange.value[0]
    && contractDateRange.value[1]
  )
));

function formatDate(date) {
  if (!(date instanceof Date)) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function draftFilters() {
  const useDateRange = !showIncorrectContractDates.value
    && Array.isArray(contractDateRange.value)
    && contractDateRange.value[0]
    && contractDateRange.value[1];
  return {
    name: employeeName.value.trim(),
    company_id: selectedCompanyId.value || null,
    status: selectedStatus.value || 'ALL',
    structure: selectedStructure.value || null,
    show_incorrect_contract_dates: showIncorrectContractDates.value,
    contract_start_date: useDateRange ? formatDate(contractDateRange.value[0]) : null,
    contract_end_date: useDateRange ? formatDate(contractDateRange.value[1]) : null,
  };
}

async function runSearch(page = 1, filters = appliedFilters.value, size = pageSize.value) {
  selectedEmployeeRow.value = null;
  const loaded = await searchEmployees({
    ...(filters || draftFilters()),
    page,
    page_size: size,
  });
  if (!loaded) return false;
  first.value = (page - 1) * size;
  pageSize.value = size;
  return true;
}

async function applyFilters() {
  if (!hasCompleteDateRange.value) return;
  appliedFilters.value = draftFilters();
  await runSearch(1, appliedFilters.value);
}

async function onPage(event) {
  await runSearch(event.page + 1, appliedFilters.value, event.rows);
}

function isIncorrectContractDate(employee) {
  if (!employee.employee_contract_date) return true;
  return employee.employee_contract_date === '1970-01-01'
    || (
      employee.employee_join_date
      && employee.employee_contract_date < employee.employee_join_date
    );
}

const showEditDialog = () => { editEmployeeDialog.value = true; };
const showAddDialog = () => { addEmployeeDialog.value = true; };
const handleBack = () => {
  editEmployeeDialog.value = false;
  addEmployeeDialog.value = false;
};

async function handleEmployeeUpdated() {
  editEmployeeDialog.value = false;
  addEmployeeDialog.value = false;
  selectedEmployeeRow.value = null;
  refreshing.value = true;
  try {
    await Promise.all([
      employeesStore.refreshEmployees(),
      runSearch(employeePagination.value.page, appliedFilters.value),
    ]);
    toast.add({
      severity: 'success',
      summary: 'Data Updated',
      detail: 'Employee data has been updated and refreshed.',
      life: 3000,
      group: 'br',
    });
  } finally {
    refreshing.value = false;
  }
}

onMounted(async () => {
  const startupResults = await Promise.allSettled([
    companiesStore.ensureCompanies(),
    structuresStore.ensureStructures(),
  ]);
  const failedStartup = startupResults.find(result => result.status === 'rejected');
  if (failedStartup) {
    const requestError = failedStartup.reason;
    toast.add({
      severity: 'warn',
      summary: 'Some filters are unavailable',
      detail: requestError.response?.data?.error || requestError.message,
      life: 12000,
      group: 'br',
    });
  }
  appliedFilters.value = draftFilters();
  await runSearch(1, appliedFilters.value);
});
</script>

<style scoped>
.employees-sticky-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: minmax(14rem, 35%) minmax(0, 65%);
  gap: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.employees-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.employee-action {
  position: fixed;
  right: 30px;
  bottom: 30px;
  z-index: 50;
}

.employee-photo {
  width: 3rem;
  height: 3rem;
  object-fit: cover;
}

@media (max-width: 1100px) {
  .employees-sticky-header {
    grid-template-columns: 1fr;
  }

  .employees-filters {
    justify-content: flex-start;
  }
}
</style>
